import React from 'react';
import { faBolt } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/dynamic-emergent-systems.jpg
import dynamicEmergentImg from '../../../assets/images/blogs/dynamic-emergent-systems.jpg';

const DynamicEmergentSystems = () => (
    <BlogPost
        title="Dynamic Emergent Systems: The Legacy Thinking Holding Us Back"
        subtitle="Everybody has a plan until they get punched in the face."
        category="Strategy"
        categoryIcon={faBolt}
        color="#003366"
        date="February 2026"
        readTime="5 min read"
        image={dynamicEmergentImg}
        imageAlt="Dynamic emergent systems - from defining upfront to learning through execution"
        ctaHeading="Ready to embrace emergence?"
        ctaText="Book a discovery call and we'll show you how the Knowledge Flywheel captures emergent behaviour algorithmically."
        seoDescription="Why the assumption that we can formalise everything before acting keeps failing, and how dynamic emergent systems learn through execution instead."
        seoKeywords="emergent systems, dynamic processes, knowledge capture, AI transformation, context graphs, Nebulyx"
        slug="dynamic-emergent-systems"
    >
        <p>
            Context Graph conversations have surfaced a key tension: "How do we deal
            with change?"
        </p>

        <p>
            How many massive migrations (data and system) do we do every year because
            systems can't cope with change?
        </p>

        <p>
            I worked on a system with a clever SME, defined all the flows upfront, UAT
            went well. But when we ran it in production it fundamentally failed.
        </p>

        <blockquote className="blog-post-quote">
            "Everybody has a plan until they get punched in the face" - Mike Tyson 🥊
        </blockquote>

        <p>
            "We've been taught that if we can simply make processes stable and documented,
            it's all good. But what we control is only about 70-80% of why and how X happens,
            and when the uncontrolled portion also is volatile and rapidly changing, the
            process rapidly loses relevance."
        </p>

        <p>
            That uncontrolled 20-30%? That's where reality lives. And you can't capture it
            upfront - it continually emerges.
        </p>

        <h3>The Assumption Holding Us Back</h3>

        <p>
            It's amazing in 2026, we still believe that we can formalise and encode
            everything before acting - despite the many failures in this mode.
        </p>

        <p>
            I.e. Interview the experts. Build the process. Document the procedures.
            Then execute.
        </p>

        <p>
            It's seductive - the illusion of control.
        </p>

        <p>
            But it assumes experts can articulate what they know and they aren't faced
            with conditions they haven't seen before.
        </p>

        <p>
            <strong>In my experience? The richest knowledge surfaces in the moment of
            decision. Not weeks later in a workshop.</strong>
        </p>

        <h3>What If We Flipped It?</h3>

        <p>
            Start with source material and goals. Let AI extract semantics directly.
        </p>

        <p>
            Capture tacit knowledge when SMEs override decisions - in the moment, with
            full context.
        </p>

        <p>
            Let the system learn as it runs - learn from its decision traces!
        </p>

        <p>
            Decision traces don't require predefined procedures to trace against. The
            trace is the execution. State, knowledge, reasoning, intent - captured in
            the moment.
        </p>

        <p>
            The "why" surfaces through action, not upfront modelling.
        </p>

        <p>
            SME &amp; algorithm working together, capturing emergent behaviour.
            Propensities that model actual behaviour algorithmically. Causal models
            that understand what actually drives outcomes.
        </p>

        <p>
            <strong>Adapting as reality moves - this is the shift.</strong> 💡
        </p>

        <h3>The Contrast</h3>

        <p>
            <strong>Legacy:</strong> Define then Execute. Massive cost and friction,
            upfront &amp; change.
        </p>

        <p>
            <strong>Emergent:</strong> Capture algorithmically, execute then learn.
            Change is the operating model.
        </p>

        <h3>The Hard Question</h3>

        <p>
            "Do we qualify out the ones not ready? Or can we bridge the gap?"
        </p>

        <p>
            The companies that get this mode exist. Revolut. Tesla. SpaceX. Starling.
        </p>

        <p>
            They bake in innovation and change as a feature, not something bolted on top.
        </p>

        <p>
            Note: If we had to define everything upfront, the start-up world wouldn't exist.
        </p>

        <p>
            The CEO doesn't want a perfect model of today. They want a system that can
            handle tomorrow.
        </p>

        <p>
            The flywheel turns. The knowledge lives, adapts, and delivers. 🎯
        </p>
    </BlogPost>
);

export default DynamicEmergentSystems;
